import React from "react";
import { Plus, Trash2, ChevronUp, ChevronDown } from "lucide-react";
import { getPath, setPath } from "@/lib/paths";

const inputCls =
  "w-full bg-transparent border border-ink/20 px-2.5 py-1.5 text-sm outline-none focus:border-ink transition-colors";

function move(arr, from, to) {
  const a = arr.slice();
  if (to < 0 || to >= a.length) return a;
  const [it] = a.splice(from, 1);
  a.splice(to, 0, it);
  return a;
}

export default function SectionFields({ fields, value, onChange, testidPrefix = "" }) {
  const val = value || {};
  const setField = (k, v) => onChange(setPath(val, k, v));

  return (
    <div className="space-y-4">
      {fields.map((f) => (
        <FieldNode
          key={f.k}
          field={f}
          value={val}
          setField={setField}
          testidPrefix={testidPrefix}
        />
      ))}
    </div>
  );
}

function FieldNode({ field, value, setField, testidPrefix }) {
  const tid = `${testidPrefix}${field.k}`;

  if (field.type === "text") {
    return (
      <label className="block">
        <span className="eyebrow text-ink/50">{field.label}</span>
        <input
          className={inputCls + " mt-1"}
          value={getPath(value, field.k) ?? ""}
          onChange={(e) => setField(field.k, e.target.value)}
          data-testid={`field-${tid}`}
        />
      </label>
    );
  }

  if (field.type === "area") {
    return (
      <label className="block">
        <span className="eyebrow text-ink/50">{field.label}</span>
        <textarea
          rows={3}
          className={inputCls + " mt-1 resize-y"}
          value={getPath(value, field.k) ?? ""}
          onChange={(e) => setField(field.k, e.target.value)}
          data-testid={`field-${tid}`}
        />
      </label>
    );
  }

  if (field.type === "group") {
    const sub = getPath(value, field.k) || {};
    return (
      <div className="border-l-2 border-ink/15 pl-4">
        <div className="eyebrow text-ink/60 mb-2">{field.label}</div>
        {React.createElement(SectionFields, {
          fields: field.fields,
          value: sub,
          onChange: (v) => setField(field.k, v),
          testidPrefix: `${tid}.`,
        })}
      </div>
    );
  }

  if (field.type === "list") {
    const arr = getPath(value, field.k) || [];
    const setArr = (a) => setField(field.k, a);
    const addItem = () => setArr([...arr, field.of === "string" ? "" : {}]);

    return (
      <div className="border border-ink/15 p-3">
        <div className="flex items-center justify-between mb-2">
          <span className="eyebrow text-ink/60">{field.label}</span>
          <button
            type="button"
            onClick={addItem}
            data-testid={`add-${tid}`}
            className="eyebrow inline-flex items-center gap-1 border border-ink px-2 py-1 hover:bg-ink hover:text-paper transition-colors"
          >
            <Plus size={12} /> Adaugă
          </button>
        </div>
        <div className="space-y-3">
          {arr.map((item, i) => (
            <div key={i} className="border border-ink/10 bg-ink/[0.02] p-2.5">
              <div className="flex items-center justify-between mb-1.5">
                <span className="eyebrow text-ink/40">#{i + 1}</span>
                <div className="flex gap-1">
                  <button type="button" onClick={() => setArr(move(arr, i, i - 1))} className="p-1 hover:bg-ink/10" title="Sus">
                    <ChevronUp size={13} />
                  </button>
                  <button type="button" onClick={() => setArr(move(arr, i, i + 1))} className="p-1 hover:bg-ink/10" title="Jos">
                    <ChevronDown size={13} />
                  </button>
                  <button
                    type="button"
                    onClick={() => setArr(arr.filter((_, j) => j !== i))}
                    data-testid={`del-${tid}-${i}`}
                    className="p-1 text-red-700 hover:bg-red-700/10"
                    title="Șterge"
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
              {field.of === "string" ? (
                <input
                  className={inputCls}
                  value={item ?? ""}
                  onChange={(e) => setArr(arr.map((x, j) => (j === i ? e.target.value : x)))}
                  data-testid={`field-${tid}-${i}`}
                />
              ) : (
                React.createElement(SectionFields, {
                  fields: field.fields,
                  value: item || {},
                  onChange: (v) => setArr(arr.map((x, j) => (j === i ? v : x))),
                  testidPrefix: `${tid}.${i}.`,
                })
              )}
            </div>
          ))}
          {arr.length === 0 && <div className="eyebrow text-ink/30 py-2">Gol</div>}
        </div>
      </div>
    );
  }

  return null;
}
